import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import type { ISelectedKnowledgeTagData } from '../KnowledgeBaseMention/components/SelectedKnowledgeTag';
import type { ISelectedSkillTagData } from '../SkillMention/SelectedSkillTag';
import {
  computeMentionTriggerRemoveEnd,
  resolveMentionTriggerIndex,
} from '../mentionShared/mentionText';
import {
  attachKnowledgePayloadsInRoot,
  attachSkillPayloadsInRoot,
  buildEditorFromSerialized,
  createMentionElement,
  getSelectionOffsets,
  insertNodeAtOffsets,
  insertTextAtCurrentSelection,
  ensureTypingSpaceAfterMention,
  ensureTypingSpaceBeforeMention,
  hasLeadingMentionElements,
  hasOnlyLeadingMentionsInValue,
  isEditorValueEmpty,
  needsEditorHydration,
  placeCaretAroundMention,
  placeCaretBeforeInlinePlaceholder,
  relocalizeMentionsInRoot,
  removeEditorInlinePlaceholder,
  removeLabelFromRoot,
  removeMentionByKey,
  replaceMentionBodyInRoot,
  replaceOffsetsWithText,
  serializeEditorRoot,
  setSelectionOffsets,
  shouldShowEditorPlaceholder,
  stripLeadingEditorEmpties,
  syncEditorInlinePlaceholder,
  syncMentionTypingGapsInRoot,
  trimRootToLength,
  tryAtomicMentionDeleteInEditor,
} from './editorDom';
import { buildKnowledgeMentionKey } from './mentionKeyUtils';
import { parseMentionBodyFromLabel } from './mentionTokenDom';
import type { IInsertMentionOptions, IMentionEditorSurface, TMentionEditorKind } from './types';
import './index.scss';

function parseIndentPx(textIndent?: string | number): number {
  if (textIndent == null || textIndent === '') return 0;
  if (typeof textIndent === 'number') return textIndent;
  const matched = /^([\d.]+)px$/.exec(String(textIndent).trim());
  return matched ? Number(matched[1]) : 0;
}

export interface IMentionEditorProps {
  value: string;
  onChange: (
    value: string,
    meta?: { anchor: number; focus: number; },
  ) => void;
  placeholder?: string;
  className?: string;
  maxLength?: number;
  maxHeight?: string;
  minHeight?: string;
  textIndent?: string | number;
  disabled?: boolean;
  isEN?: boolean;
  kbDocUnit?: string;
  selectedKnowledge?: ISelectedKnowledgeTagData[];
  selectedSkills?: ISelectedSkillTagData[];
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  onCompositionStart?: () => void;
  onCompositionEnd?: () => void;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onSelectionChange?: (start: number, end: number) => void;
  /** 编辑态：保持 display_query 折叠文案，不因 payload 重算 mention 正文 */
  freezeMentionLabels?: boolean;
}

function resolveMentionKey(
  kind: TMentionEditorKind,
  options?: IInsertMentionOptions,
): string {
  if (kind === 'knowledge' && options?.knowledgeItems?.length) {
    return buildKnowledgeMentionKey(options.knowledgeItems);
  }
  return `${kind}:${Date.now()}`;
}

/**
 * Mention 编辑器：DOM 为编辑区唯一数据源。
 * placeholder 仅为 wrap 上的视觉层（::before），不参与编辑、不抢光标。
 */
const MentionEditor = forwardRef<IMentionEditorSurface, IMentionEditorProps>(
  (
    {
      value,
      onChange,
      placeholder,
      className,
      maxLength,
      maxHeight,
      minHeight,
      textIndent,
      disabled = false,
      isEN = false,
      kbDocUnit = '篇',
      selectedKnowledge = [],
      selectedSkills = [],
      onFocus,
      onBlur,
      onKeyDown,
      onPaste,
      onCompositionStart,
      onCompositionEnd,
      onMouseDown,
      onSelectionChange,
      freezeMentionLabels = false,
    },
    ref,
  ) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const localeRef = useRef({ kbDocUnit, isEN });
    const pinnedOffsetRef = useRef<number | null>(null);
    const composingRef = useRef(false);
    const lastEmittedRef = useRef<string>('');

    localeRef.current = { kbDocUnit, isEN };

    const [showPlaceholder, setShowPlaceholder] = useState(
      () => shouldShowEditorPlaceholder(null, value) && Boolean(placeholder),
    );

    const useInlinePlaceholder = useMemo(
      () => showPlaceholder && hasOnlyLeadingMentionsInValue(value || ''),
      [showPlaceholder, value],
    );

    const applyEmptyPlaceholderIndent = useCallback(() => {
      wrapRef.current?.style.setProperty(
        '--dba-mention-editor-placeholder-indent',
        `${parseIndentPx(textIndent)}px`,
      );
    }, [textIndent]);

    const fixInlinePlaceholderCaret = useCallback(() => {
      const root = rootRef.current;
      if (root?.querySelector('.dba-mention-editor-inline-placeholder')) {
        placeCaretBeforeInlinePlaceholder(root);
      }
    }, []);

    const syncPlaceholderVisibility = useCallback(
      (serialized: string) => {
        const root = rootRef.current;
        const visible =
          shouldShowEditorPlaceholder(root, serialized) && Boolean(placeholder);

        setShowPlaceholder(visible);

        if (!root || !placeholder) return;

        const useInline = visible && hasLeadingMentionElements(root);
        if (useInline) {
          syncEditorInlinePlaceholder(root, placeholder);
        } else {
          removeEditorInlinePlaceholder(root);
          if (visible) applyEmptyPlaceholderIndent();
        }
      },
      [placeholder, applyEmptyPlaceholderIndent],
    );

    const surfaceRef = useRef<IMentionEditorSurface>({
      value: '',
      selectionStart: 0,
      selectionEnd: 0,
      focus: () => rootRef.current?.focus(),
      setSelectionRange: () => {},
      getRootElement: () => rootRef.current,
      insertMentionAtTrigger: () => {},
      replaceMentionBody: () => {},
      insertMentionAtSelection: () => {},
      insertAtSelection: () => {},
      saveSelection: () => {},
      getPanelInsertOffset: () => null,
      clearPanelInsertOffset: () => {},
      relocalizeMentions: () => {},
      syncKnowledgeMentionTags: () => {},
      resetEditor: () => {},
      forceHydrateFromValue: () => {},
    });

    const emitChange = useCallback(
      (explicitSelection?: { anchor: number; focus: number; }) => {
        const root = rootRef.current;
        if (!root) return;
        stripLeadingEditorEmpties(root);
        syncMentionTypingGapsInRoot(root);
        let serialized = serializeEditorRoot(root);
        if (isEditorValueEmpty(serialized)) {
          if (root.childNodes.length > 0) {
            buildEditorFromSerialized(root, '');
          }
          serialized = '';
        }
        const offsets = explicitSelection
          ? { start: explicitSelection.anchor, end: explicitSelection.focus }
          : getSelectionOffsets(root);
        lastEmittedRef.current = serialized;
        surfaceRef.current.value = serialized;
        surfaceRef.current.selectionStart = offsets.start;
        surfaceRef.current.selectionEnd = offsets.end;
        onSelectionChange?.(offsets.start, offsets.end);
        syncPlaceholderVisibility(serialized);
        onChange(serialized, { anchor: offsets.start, focus: offsets.end });
      },
      [onChange, onSelectionChange, syncPlaceholderVisibility],
    );

    const resolveInsertOffset = useCallback((): number => {
      if (pinnedOffsetRef.current != null) {
        const offset = pinnedOffsetRef.current;
        pinnedOffsetRef.current = null;
        return offset;
      }
      const root = rootRef.current;
      return root ? getSelectionOffsets(root).start : 0;
    }, []);

    const insertMentionAtTrigger = useCallback(
      (
        triggerIndex: number,
        body: string,
        kind: TMentionEditorKind,
        mentionKey: string,
        triggerChar?: string,
        options?: IInsertMentionOptions,
      ) => {
        const root = rootRef.current;
        if (!root || triggerIndex < 0) return;
        const resolvedKey = mentionKey || resolveMentionKey(kind, options);
        const resolvedTriggerChar =
          triggerChar || (kind === 'knowledge' ? '@' : '/');
        const serialized = serializeEditorRoot(root);
        const selectionEnd = resolveInsertOffset();
        const resolvedTriggerIndex = resolveMentionTriggerIndex(
          serialized,
          resolvedTriggerChar,
          triggerIndex,
          selectionEnd,
        );
        const removeEnd = computeMentionTriggerRemoveEnd(
          serialized,
          resolvedTriggerIndex,
          selectionEnd,
        );
        const trimmedBody = body.trim();
        root.focus();
        if (!trimmedBody) {
          replaceOffsetsWithText(root, resolvedTriggerIndex, removeEnd, '');
          emitChange();
          return;
        }
        const node = createMentionElement(
          trimmedBody,
          kind,
          resolvedKey,
          options?.knowledgeItems,
          options?.skillPayload,
        );
        insertNodeAtOffsets(root, resolvedTriggerIndex, removeEnd, node);
        ensureTypingSpaceBeforeMention(root, node);
        ensureTypingSpaceAfterMention(root, node);
        emitChange();
      },
      [emitChange, resolveInsertOffset],
    );

    const insertMentionAtSelection = useCallback(
      (
        body: string,
        kind: TMentionEditorKind,
        mentionKey: string,
        options?: IInsertMentionOptions,
      ) => {
        const root = rootRef.current;
        const trimmedBody = body.trim();
        if (!root || !trimmedBody) return;
        const resolvedKey = mentionKey || resolveMentionKey(kind, options);
        const offset = options?.usePinnedOffset
          ? resolveInsertOffset()
          : getSelectionOffsets(root).start;
        const node = createMentionElement(
          trimmedBody,
          kind,
          resolvedKey,
          options?.knowledgeItems,
          options?.skillPayload,
        );
        root.focus();
        insertNodeAtOffsets(root, offset, offset, node);
        ensureTypingSpaceBeforeMention(root, node);
        ensureTypingSpaceAfterMention(root, node);
        emitChange();
      },
      [emitChange, resolveInsertOffset],
    );

    const replaceMentionBody = useCallback(
      (
        oldBody: string,
        newBody: string,
        kind: TMentionEditorKind,
        options?: Pick<IInsertMentionOptions, 'knowledgeItems'>,
      ) => {
        const root = rootRef.current;
        if (!root) return;
        replaceMentionBodyInRoot(root, oldBody, newBody, kind);
        if (kind === 'knowledge' && options?.knowledgeItems?.length) {
          attachKnowledgePayloadsInRoot(root, options.knowledgeItems, localeRef.current.kbDocUnit);
        }
        emitChange();
      },
      [emitChange],
    );

    const insertAtSelection = useCallback(
      (text: string) => {
        const root = rootRef.current;
        if (!root || !text) return;
        root.focus();
        insertTextAtCurrentSelection(root, text);
        emitChange();
      },
      [emitChange],
    );

    const relocalizeMentions = useCallback(() => {
      const root = rootRef.current;
      if (!root) return;
      const changed = relocalizeMentionsInRoot(root, localeRef.current);
      if (changed) emitChange();
    }, [emitChange]);

    const resetEditor = useCallback(() => {
      const root = rootRef.current;
      if (!root) return;
      buildEditorFromSerialized(root, '');
      lastEmittedRef.current = '';
      surfaceRef.current.value = '';
      surfaceRef.current.selectionStart = 0;
      surfaceRef.current.selectionEnd = 0;
      syncPlaceholderVisibility('');
    }, [syncPlaceholderVisibility]);

    const forceHydrateFromValue = useCallback((
      text: string,
      knowledgeItems?: ISelectedKnowledgeTagData[],
      options?: { cursorAtEnd?: boolean; },
    ) => {
      const root = rootRef.current;
      if (!root) return;
      const nextValue = text || '';
      buildEditorFromSerialized(root, nextValue);
      attachKnowledgePayloadsInRoot(
        root,
        knowledgeItems?.length ? knowledgeItems : selectedKnowledge,
        kbDocUnit,
      );
      attachSkillPayloadsInRoot(root, selectedSkills, isEN);
      if (!freezeMentionLabels) {
        relocalizeMentionsInRoot(root, localeRef.current);
      }
      const serialized = serializeEditorRoot(root);
      lastEmittedRef.current = serialized;
      surfaceRef.current.value = serialized;
      syncPlaceholderVisibility(serialized);
      if (options?.cursorAtEnd) {
        const end = serialized.length;
        setSelectionOffsets(root, end, end);
        surfaceRef.current.selectionStart = end;
        surfaceRef.current.selectionEnd = end;
      }
    }, [selectedKnowledge, selectedSkills, kbDocUnit, isEN, syncPlaceholderVisibility, freezeMentionLabels]);

    surfaceRef.current.setSelectionRange = (start, end) => {
      const root = rootRef.current;
      if (!root) return;
      setSelectionOffsets(root, start, end);
      surfaceRef.current.selectionStart = start;
      surfaceRef.current.selectionEnd = end;
    };
    surfaceRef.current.insertMentionAtTrigger = insertMentionAtTrigger;
    surfaceRef.current.insertMentionAtSelection = insertMentionAtSelection;
    surfaceRef.current.replaceMentionBody = replaceMentionBody;
    surfaceRef.current.insertAtSelection = insertAtSelection;
    surfaceRef.current.saveSelection = () => {
      const root = rootRef.current;
      pinnedOffsetRef.current = root ? getSelectionOffsets(root).start : null;
    };
    surfaceRef.current.getPanelInsertOffset = () => pinnedOffsetRef.current;
    surfaceRef.current.clearPanelInsertOffset = () => {
      pinnedOffsetRef.current = null;
    };
    surfaceRef.current.relocalizeMentions = relocalizeMentions;
    surfaceRef.current.resetEditor = resetEditor;
    surfaceRef.current.forceHydrateFromValue = forceHydrateFromValue;
    surfaceRef.current.focus = () => rootRef.current?.focus();
    surfaceRef.current.getRootElement = () => rootRef.current;

    useImperativeHandle(ref, () => surfaceRef.current, []);

    useLayoutEffect(() => {
      applyEmptyPlaceholderIndent();
    }, [applyEmptyPlaceholderIndent]);

    useLayoutEffect(() => {
      const root = rootRef.current;
      if (!root) return;
      const nextValue = value || '';
      if (nextValue === lastEmittedRef.current) {
        syncPlaceholderVisibility(nextValue);
        return;
      }
      if (!needsEditorHydration(root, nextValue)) {
        lastEmittedRef.current = nextValue;
        syncPlaceholderVisibility(nextValue);
        return;
      }
      buildEditorFromSerialized(root, nextValue);
      attachKnowledgePayloadsInRoot(root, selectedKnowledge, kbDocUnit);
      attachSkillPayloadsInRoot(root, selectedSkills, isEN);
      if (!freezeMentionLabels) {
        relocalizeMentionsInRoot(root, localeRef.current);
      }
      const serialized = serializeEditorRoot(root);
      lastEmittedRef.current = serialized;
      surfaceRef.current.value = serialized;
      syncPlaceholderVisibility(serialized);
      if (!freezeMentionLabels && serialized !== nextValue) {
        const offsets = getSelectionOffsets(root);
        onChange(serialized, { anchor: offsets.start, focus: offsets.end });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, syncPlaceholderVisibility, freezeMentionLabels]);

    useLayoutEffect(() => {
      const root = rootRef.current;
      if (!root) return;
      attachKnowledgePayloadsInRoot(root, selectedKnowledge, kbDocUnit);
      attachSkillPayloadsInRoot(root, selectedSkills, isEN);
      if (freezeMentionLabels) {
        syncPlaceholderVisibility(surfaceRef.current.value || value || '');
        return;
      }
      const changed = relocalizeMentionsInRoot(root, localeRef.current);
      if (changed) emitChange();
      else syncPlaceholderVisibility(surfaceRef.current.value || value || '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kbDocUnit, isEN, selectedKnowledge, selectedSkills, syncPlaceholderVisibility, value, freezeMentionLabels]);

    useEffect(() => {
      const root = rootRef.current;
      if (!root) return;
      const handleDocSelectionChange = () => {
        if (composingRef.current) return;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        if (!root.contains(sel.getRangeAt(0).commonAncestorContainer)) return;
        const offsets = getSelectionOffsets(root);
        surfaceRef.current.selectionStart = offsets.start;
        surfaceRef.current.selectionEnd = offsets.end;
        onSelectionChange?.(offsets.start, offsets.end);
      };
      document.addEventListener('selectionchange', handleDocSelectionChange);
      return () => document.removeEventListener('selectionchange', handleDocSelectionChange);
    }, [onSelectionChange]);

    const handleInput = useCallback(() => {
      if (composingRef.current || disabled) return;
      const root = rootRef.current;
      if (!root) return;
      removeEditorInlinePlaceholder(root);
      if (maxLength) {
        const raw = serializeEditorRoot(root);
        if (raw.length > maxLength) {
          trimRootToLength(root, maxLength);
        }
      }
      emitChange();
    }, [disabled, emitChange, maxLength]);

    const handleKeyDownInternal = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) {
        onKeyDown?.(e);
        return;
      }
      onKeyDown?.(e);
      if (e.defaultPrevented || composingRef.current) return;

      if (e.key === 'Backspace' || e.key === 'Delete') {
        const root = rootRef.current;
        if (root) {
          const result = tryAtomicMentionDeleteInEditor(
            root,
            e.key as 'Backspace' | 'Delete',
          );
          if (result?.handled) {
            e.preventDefault();
            lastEmittedRef.current = result.nextValue;
            surfaceRef.current.value = result.nextValue;
            surfaceRef.current.selectionStart = result.cursor;
            surfaceRef.current.selectionEnd = result.cursor;
            onSelectionChange?.(result.cursor, result.cursor);
            syncPlaceholderVisibility(result.nextValue);
            onChange(result.nextValue, {
              anchor: result.cursor,
              focus: result.cursor,
            });
            return;
          }
        }
      }

      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        const root = rootRef.current;
        if (root) {
          insertTextAtCurrentSelection(root, '\n');
          emitChange();
        }
      }
    };

    const handlePasteInternal = (e: React.ClipboardEvent<HTMLDivElement>) => {
      const items = e.clipboardData?.items;
      let hasFile = false;
      if (items) {
        for (const item of items) {
          if (item.kind === 'file') {
            hasFile = true;
            break;
          }
        }
      }
      if (hasFile) {
        onPaste?.(e);
        return;
      }
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      if (!text) return;
      const root = rootRef.current;
      if (!root) return;
      insertTextAtCurrentSelection(root, text);
      handleInput();
    };

    const handleCompositionStartInternal = () => {
      composingRef.current = true;
      setShowPlaceholder(false);
      if (rootRef.current) removeEditorInlinePlaceholder(rootRef.current);
      onCompositionStart?.();
    };

    const handleCompositionEndInternal = () => {
      composingRef.current = false;
      onCompositionEnd?.();
      handleInput();
    };

    const handleMentionMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const root = rootRef.current;
      const removeEl = target.closest('[data-mention-action="remove"]');
      if (removeEl) {
        e.preventDefault();
        e.stopPropagation();
        const token = removeEl.closest('.dba-mention-token') as HTMLElement | null;
        const mentionKey = token?.getAttribute('data-mention-key');
        if (mentionKey && root) {
          removeMentionByKey(root, mentionKey);
          emitChange();
        }
        return;
      }

      const token = target.closest('.dba-mention-token') as HTMLElement | null;
      if (token && root?.contains(token)) {
        e.preventDefault();
        const mentionKey = token.getAttribute('data-mention-key') || '';
        const rect = token.getBoundingClientRect();
        const placeBefore = e.clientX < rect.left + rect.width / 2;
        if (placeCaretAroundMention(root, mentionKey, placeBefore)) {
          if (!placeBefore) {
            ensureTypingSpaceAfterMention(root, token);
          }
          const offsets = getSelectionOffsets(root);
          surfaceRef.current.selectionStart = offsets.start;
          surfaceRef.current.selectionEnd = offsets.end;
        }
        root.focus();
        return;
      }

      onMouseDown?.(e);

      if (root?.querySelector('.dba-mention-editor-inline-placeholder')) {
        requestAnimationFrame(() => {
          if (document.activeElement === root) fixInlinePlaceholderCaret();
        });
      }
    };

    return (
      <div
        ref={wrapRef}
        className={classNames('dba-mention-editor-wrap', {
          'dba-mention-editor-wrap--show-placeholder':
            showPlaceholder && !useInlinePlaceholder,
        })}
        data-placeholder={placeholder}
      >
        <div
          ref={rootRef}
          className={classNames('dba-mention-editor', 'dba-input-box', className)}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          style={{
            maxHeight,
            minHeight,
            textIndent: textIndent as React.CSSProperties['textIndent'],
          }}
          onInput={handleInput}
          onFocus={() => {
            fixInlinePlaceholderCaret();
            onFocus?.();
          }}
          onBlur={onBlur}
          onKeyDown={handleKeyDownInternal}
          onPaste={handlePasteInternal}
          onCompositionStart={handleCompositionStartInternal}
          onCompositionEnd={handleCompositionEndInternal}
          onMouseDown={handleMentionMouseDown}
        />
      </div>
    );
  },
);

MentionEditor.displayName = 'MentionEditor';

export default MentionEditor;

export { removeLabelFromRoot, parseMentionBodyFromLabel };
